import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { FadeUp, SectionLabel } from "../components/Motion";
import { getProjects } from "../services/projectsApi";
import { mapApiProject } from "../utils/projectMapper";

export default function Gallery() {
  const [projects, setProjects] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedProject, setSelectedProject] = useState("all");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadProjects() {
      try {
        setLoading(true);
        setError("");

        const apiProjects = await getProjects();
        const mappedProjects = apiProjects.map(mapApiProject);

        setProjects(mappedProjects);
      } catch (err) {
        console.error("Gallery loading error:", err);
        setError("Unable to load gallery images.");
      } finally {
        setLoading(false);
      }
    }

    loadProjects();
  }, []);

  const allProjectGalleries = useMemo(() => {
    return projects.reduce((accumulator, project) => {
      if (
        Array.isArray(project.galleryImages) &&
        project.galleryImages.length > 0
      ) {
        accumulator[project.slug] = {
          id: project.id,
          name: project.name,
          images: project.galleryImages,
        };
      }

      return accumulator;
    }, {});
  }, [projects]);

  const filteredImages = useMemo(() => {
    if (selectedProject === "all") {
      return projects.flatMap((project) =>
        Array.isArray(project.galleryImages) ? project.galleryImages : []
      );
    }

    return allProjectGalleries[selectedProject]?.images || [];
  }, [projects, selectedProject, allProjectGalleries]);

  function selectProject(projectSlug) {
    setSelectedProject(projectSlug);
    setCurrentImageIndex(0);
    setSelectedImage(null);
  }

  function openLightbox(index) {
    setCurrentImageIndex(index);
    setSelectedImage(filteredImages[index]);
  }

  function closeLightbox() {
    setSelectedImage(null);
  }

  function goToPrevious(event) {
    event?.stopPropagation();

    if (filteredImages.length === 0) {
      return;
    }

    const newIndex =
      currentImageIndex === 0
        ? filteredImages.length - 1
        : currentImageIndex - 1;

    setCurrentImageIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  }

  function goToNext(event) {
    event?.stopPropagation();

    if (filteredImages.length === 0) {
      return;
    }

    const newIndex =
      currentImageIndex === filteredImages.length - 1
        ? 0
        : currentImageIndex + 1;

    setCurrentImageIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  }

  useEffect(() => {
    function handleKeyDown(event) {
      if (!selectedImage) {
        return;
      }

      if (event.key === "ArrowLeft") {
        goToPrevious();
      }

      if (event.key === "ArrowRight") {
        goToNext();
      }

      if (event.key === "Escape") {
        closeLightbox();
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  });

  return (
    <div
      data-testid="gallery-page"
      className="min-h-screen bg-[#FAFAFA]"
    >
      {/* Hero section */}
      <section className="relative min-h-[50vh] bg-gradient-to-r from-[#1A1A1A] via-[#2b1a24] to-[#1A1A1A] text-white pt-32 pb-20">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-20 right-10 h-72 w-72 rounded-full bg-[#7A2E5D]/30 blur-3xl" />

          <div className="absolute bottom-10 left-5 h-96 w-96 rounded-full bg-[#E85D8E]/20 blur-3xl" />
        </div>

        <div className="container mx-auto px-6 md:px-12 max-w-7xl relative z-10">
          <FadeUp className="text-center">
            <SectionLabel className="text-[#E85D8E] tracking-[0.3em] uppercase mb-6">
              Our Gallery
            </SectionLabel>

            <h1 className="font-serif text-5xl md:text-7xl text-white leading-tight mb-6">
              Moments of{" "}
              <span className="italic brand-gradient-text">
                Craft
              </span>
            </h1>

            <p className="text-[#ccc] text-lg md:text-xl max-w-2xl mx-auto">
              Explore our portfolio of carefully executed projects
              showcasing quality construction and attention to detail.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Filters and gallery */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <FadeUp className="mb-12">
            <div className="flex flex-wrap gap-3 justify-center md:justify-start items-center">
              <p className="text-sm font-medium text-[#555] uppercase tracking-wide">
                Filter:
              </p>

              <button
                type="button"
                onClick={() => selectProject("all")}
                className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 ${
                  selectedProject === "all"
                    ? "bg-[#7A2E5D] text-white shadow-lg"
                    : "bg-white text-[#1A1A1A] border border-[#E5E5E5] hover:border-[#7A2E5D] hover:text-[#7A2E5D]"
                }`}
              >
                All Projects
              </button>

              {Object.entries(allProjectGalleries).map(
                ([slug, projectGallery]) => (
                  <button
                    key={projectGallery.id || slug}
                    type="button"
                    onClick={() => selectProject(slug)}
                    className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 ${
                      selectedProject === slug
                        ? "bg-[#7A2E5D] text-white shadow-lg"
                        : "bg-white text-[#1A1A1A] border border-[#E5E5E5] hover:border-[#7A2E5D] hover:text-[#7A2E5D]"
                    }`}
                  >
                    {projectGallery.name}
                  </button>
                )
              )}
            </div>

            {!loading && !error && (
              <p className="text-sm text-[#888] mt-6">
                Showing{" "}
                <span className="font-semibold text-[#1A1A1A]">
                  {filteredImages.length}
                </span>{" "}
                images
              </p>
            )}
          </FadeUp>

          {loading && (
            <p className="text-center text-[#555] py-20">
              Loading gallery...
            </p>
          )}

          {!loading && error && (
            <p className="text-center text-red-600 py-20">
              {error}
            </p>
          )}

          {!loading && !error && filteredImages.length > 0 && (
            <div className="masonry columns-1 sm:columns-2 lg:columns-3 gap-6">
              {filteredImages.map((imageUrl, index) => (
                <motion.div
                  key={`${selectedProject}-${imageUrl}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (index % 6) * 0.05 }}
                  onClick={() => openLightbox(index)}
                  className="group cursor-pointer overflow-hidden rounded-[20px] break-inside-avoid mb-6"
                >
                  <div className="relative overflow-hidden bg-[#E5E5E5] h-72 sm:h-80 md:h-96">
                    <img
                      src={imageUrl}
                      alt={`Gallery ${index + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-in-out"
                    />

                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="h-12 w-12 rounded-full bg-white/90 flex items-center justify-center">
                          <ChevronRight className="h-6 w-6 text-[#7A2E5D]" />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {!loading && !error && filteredImages.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[#888] text-lg">
                No images available for this project yet.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button
              type="button"
              onClick={closeLightbox}
              className="absolute top-6 right-6 z-[60] h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 backdrop-blur-sm border border-white/20"
              aria-label="Close gallery image"
            >
              <X className="h-6 w-6 text-white" />
            </button>

            <button
              type="button"
              onClick={goToPrevious}
              className="absolute left-3 md:left-6 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 backdrop-blur-sm border border-white/20 hover:scale-110"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-6 w-6 text-white" />
            </button>

            <motion.div
              key={selectedImage}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
              }}
              onClick={(event) => event.stopPropagation()}
              className="relative max-w-4xl max-h-[90vh] flex items-center justify-center"
            >
              <img
                src={selectedImage}
                alt="Gallery full view"
                className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl shadow-2xl"
              />
            </motion.div>

            <button
              type="button"
              onClick={goToNext}
              className="absolute right-3 md:right-6 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 backdrop-blur-sm border border-white/20 hover:scale-110"
              aria-label="Next image"
            >
              <ChevronRight className="h-6 w-6 text-white" />
            </button>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-md px-5 py-2.5 rounded-full border border-white/20">
              <p className="text-white font-medium">
                {currentImageIndex + 1} / {filteredImages.length}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Information section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl text-center">
          <FadeUp>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mb-6">
              Celebrating Excellence
            </h2>

            <p className="text-[#555] text-lg leading-relaxed max-w-3xl mx-auto">
              Each image in our gallery represents our commitment to
              quality craftsmanship, attention to detail, and building
              spaces that stand the test of time.
            </p>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
